import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { forwardRef } from "react";

interface PhoneMockupProps {
  children?: React.ReactNode;
  className?: string;
  screenClassName?: string;
  showNotch?: boolean;
  isSkeleton?: boolean;
  followTheme?: boolean;
}

export function PhoneSkeletonScreen() {
  return (
    <div className="flex flex-col h-full py-6 px-5 gap-3">
      <Skeleton className="h-5 w-20 rounded-full" />
      <div className="flex flex-col gap-3 flex-1">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-16 w-full rounded-lg" />
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-32 w-full rounded-lg" />
      </div>
      <div className="flex justify-between items-center">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-3 w-8" />
      </div>
    </div>
  );
}

const PhoneMockup = forwardRef<HTMLDivElement, PhoneMockupProps>(
  ({ children, className, screenClassName, showNotch = true, isSkeleton = false, followTheme = false }, ref) => {
    const borderTone = followTheme
      ? "border-neutral-300 dark:border-neutral-700"
      : "border-neutral-300";
    return (
      <div
        className={cn(
          "rounded-[2.4rem] flex flex-col border w-87 h-175 p-[6px]",
          followTheme ? "bg-background" : "light bg-white",
          borderTone,
          className,
        )}
      >
        <div
          ref={ref}
          aria-label="inner-screen"
          className={cn("relative self-stretch h-full rounded-[2rem] border overflow-hidden", borderTone, screenClassName)}
        >
          {showNotch && (
            <div className="absolute justify-center items-center flex gap-1 pt-2 flex-row top-0 left-0 right-0 z-30">
              <div className={cn("rounded-4xl h-4 w-14 border", borderTone)} />
              <div className={cn("rounded-4xl h-4 w-4 border", borderTone)} />
            </div>
          )}
          {isSkeleton ? <PhoneSkeletonScreen /> : children}
        </div>
      </div>
    );
  },
);

PhoneMockup.displayName = "PhoneMockup";

export default PhoneMockup;
